/**
 * QueueStatusPanel — friendly place-in-line display while a request waits
 * for the single inference slot.
 *
 * Only ever rendered when the server actually made this request wait (an
 * immediately admitted request never receives queue events, so
 * message.queueStatus is never set). Counts down locally between the
 * server's 5s ticks, which re-anchor the estimate.
 */

import { useState, useEffect } from 'react';
import type { QueueStatus } from '@state/chat';
import { LoadingSpinner } from '@components/icons';

interface QueueStatusPanelProps {
  status: QueueStatus;
}

export function QueueStatusPanel({ status }: QueueStatusPanelProps) {
  const remainingSec = useCountdown(status);

  return (
    <div className="queue-status" data-testid="queue-status">
      <div className="queue-status-headline">
        <LoadingSpinner className="h-4 w-4 shrink-0 text-cyan-500" />
        <span>
          {status.position === 1
            ? "You're next in line"
            : `You're #${status.position} in line`}
        </span>
      </div>
      <div className="queue-status-wait">
        {remainingSec > 0
          ? `Estimated wait: ${formatWait(remainingSec)}`
          : 'Any moment now…'}
      </div>
      <p className="queue-status-explainer">
        skjv.ai is free to study with. Answers come from a single dedicated
        Bible-study computer, and it gives each seeker its full attention
        &mdash; one at a time. Thank you for your patience.
      </p>
    </div>
  );
}

/** Seconds remaining, interpolated locally between server ticks. */
function useCountdown(status: QueueStatus): number {
  const compute = () => {
    if (status.estimatedWaitMs == null) return 0;
    const elapsed = Date.now() - status.receivedAt;
    return Math.max(0, Math.round((status.estimatedWaitMs - elapsed) / 1000));
  };
  const [remaining, setRemaining] = useState(compute);

  useEffect(() => {
    setRemaining(compute());
    const interval = setInterval(() => setRemaining(compute()), 1000);
    return () => clearInterval(interval);
    // Re-anchor whenever a fresh server tick lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.estimatedWaitMs, status.receivedAt]);

  return remaining;
}

function formatWait(totalSec: number): string {
  if (totalSec < 60) return `about ${totalSec} seconds`;
  const min = Math.round(totalSec / 60);
  return min === 1 ? 'about a minute' : `about ${min} minutes`;
}
